import { parseFilterKey } from './filter-operators.ts';
import { generateColumnLabel, isCustomLikeField } from './label-generation.ts';

type Json = Record<string, unknown>;

/** A custom-field key a saved report references, with a label for display. */
export interface PartnerSpecificField {
  key: string;
  label: string;
}

function asRecord(value: unknown): Json | undefined {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Json) : undefined;
}

function keyList(value: unknown): string[] {
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((key) => key.trim())
      .filter(Boolean);
  }
  if (Array.isArray(value)) {
    return value.filter((key): key is string => typeof key === 'string' && key.length > 0);
  }
  return [];
}

function keysOf(value: unknown): string[] {
  const record = asRecord(value);
  return record ? Object.keys(record) : [];
}

/**
 * Every field key a search config refers to, in the order the builders store
 * them. Covers both saved shapes: the TAS programs page (`filters`,
 * `chartSettings`, `aggregationTableSettings`) and the report builder
 * (`buildFilters`, `userFilters`, `groupBy`).
 */
function referencedFieldKeys(searchConfig: Json): string[] {
  const tableSettings = asRecord(searchConfig['tableSettings']);
  const filters = asRecord(searchConfig['filters']);
  const chartSettings = asRecord(searchConfig['chartSettings']);
  const aggregation = asRecord(searchConfig['aggregationTableSettings']);
  const chartMetadata = asRecord(searchConfig['metadata']);
  const buildFilters = asRecord(searchConfig['buildFilters']);
  const userFilters = asRecord(searchConfig['userFilters']);
  const groupBy = asRecord(searchConfig['groupBy']);
  const metrics = Array.isArray(aggregation?.['metrics']) ? aggregation['metrics'] : [];

  return [
    ...keyList(tableSettings?.['selectedColumns']),
    ...keyList(tableSettings?.['columnOrder']),
    ...keyList(filters?.['requiredExistsFields']),
    ...keyList(filters?.['selectedField']),
    ...keysOf(filters),
    ...keyList(chartSettings?.['selectedField']),
    ...keyList(chartSettings?.['sumField']),
    ...keyList(aggregation?.['groupByField']),
    ...metrics.flatMap((metric) => keyList(asRecord(metric)?.['field'])),
    ...keyList(chartMetadata?.['groupByField']),
    ...keyList(searchConfig['selectedGroupByField']),
    ...keyList(buildFilters?.['selectedFilters']),
    ...keysOf(buildFilters?.['filterValues']).map((key) => parseFilterKey(key).baseKey),
    ...keyList(userFilters?.['selectedFilters']),
    ...keysOf(userFilters?.['filterValues']).map((key) => parseFilterKey(key).baseKey),
    ...keyList(groupBy?.['selectedFields']),
    ...keysOf(groupBy?.['columnAggregations']),
  ];
}

/**
 * Lists the partner-specific (custom) fields a saved report's search config
 * references. Custom fields — TAS template custom fields and eligibility-file
 * employee fields — are defined per partner company, so a report that uses
 * them only resolves those columns and filters for partners that define the
 * same fields.
 *
 * Pure and non-mutating. Unknown or malformed config sections are skipped.
 *
 * @param searchConfig the report's stored `searchConfig`; null or undefined
 *   yields an empty list.
 * @returns unique custom-field keys in first-seen order, each labelled from the
 *   report's own column label override when it has one, otherwise from the
 *   generated column label.
 */
export function partnerSpecificFields(
  searchConfig: Record<string, unknown> | null | undefined
): PartnerSpecificField[] {
  if (!searchConfig) return [];

  const columnLabels = asRecord(asRecord(searchConfig['tableSettings'])?.['columnLabels']) ?? {};
  const keys = new Set(referencedFieldKeys(searchConfig).filter(isCustomLikeField));

  return [...keys].map((key) => {
    const override = columnLabels[key];
    return {
      key,
      label: typeof override === 'string' && override ? override : generateColumnLabel(key),
    };
  });
}

/**
 * Decides whether sharing a report reaches partners other than the one it was
 * built for, which is when its partner-specific fields may not resolve.
 *
 * @param sourceCompanyId the company the report was built or published for
 *   (`searchConfig.companyId`, else `metadata.publishedCompanyId`); null when it
 *   was built without a company.
 * @param targetCompanyIds the companies the share grants; null means all
 *   partners.
 * @returns true unless the target is exactly the source company.
 */
export function sharesBeyondSourceCompany(
  sourceCompanyId: string | null | undefined,
  targetCompanyIds: ReadonlyArray<string | number> | null
): boolean {
  if (targetCompanyIds === null) return true;
  if (!sourceCompanyId) return targetCompanyIds.length > 0;
  return targetCompanyIds.some((id) => String(id) !== String(sourceCompanyId));
}

/**
 * The company a saved report was built or published for.
 *
 * @param report any object carrying the report's `searchConfig` and `metadata`.
 * @returns the company id as a string, or null for a report built without one.
 */
export function sourceCompanyIdFor(report: {
  searchConfig?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
}): string | null {
  const companyId = report.searchConfig?.['companyId'] || report.metadata?.['publishedCompanyId'];
  return companyId ? String(companyId) : null;
}
